/**
 * Pure, immutable operations on the Design. Unchanged objects keep their identity, which keeps
 * undo snapshots small and lets the stitch cache skip unchanged objects.
 */
import { newId } from '../engine/model/design';
import { localBBox } from '../engine/model/geometry';
import { defaultParams } from '../engine/model/params';
import type { Design, EmbObject, StitchType, ThreadColor, Transform } from '../engine/model/types';
import type { Vec } from '../engine/geometry/vec';

export function updateObjects(d: Design, ids: Iterable<string>, f: (o: EmbObject) => EmbObject): Design {
  const set = new Set(ids);
  let changed = false;
  const objects = d.objects.map((o) => {
    if (!set.has(o.id)) return o;
    const n = f(o);
    if (n !== o) changed = true;
    return n;
  });
  return changed ? { ...d, objects } : d;
}

export const updateObject = (d: Design, id: string, patch: Partial<EmbObject>): Design =>
  updateObjects(d, [id], (o) => ({ ...o, ...patch }));

export function addObjects(d: Design, objs: EmbObject[], index = d.objects.length): Design {
  const objects = d.objects.slice();
  objects.splice(index, 0, ...objs);
  return { ...d, objects };
}

export function deleteObjects(d: Design, ids: Iterable<string>): Design {
  const set = new Set(ids);
  const objects = d.objects.filter((o) => !set.has(o.id));
  return objects.length === d.objects.length ? d : { ...d, objects };
}

/** Copies objects with new ids, offset by `offset` mm, inserted after the last original. */
export function cloneObjects(objs: EmbObject[], offset: Vec = { x: 3, y: 3 }): EmbObject[] {
  return objs.map((o) => ({
    ...structuredClone(o),
    id: newId('o'),
    name: o.name.endsWith(' (kopi)') ? o.name : `${o.name} (kopi)`,
    transform: { ...o.transform, x: o.transform.x + offset.x, y: o.transform.y + offset.y },
    entry: o.entry ? { x: o.entry.x + offset.x, y: o.entry.y + offset.y } : null,
    exit: o.exit ? { x: o.exit.x + offset.x, y: o.exit.y + offset.y } : null,
    locked: false,
  }));
}

export function duplicateObjects(d: Design, ids: string[]): { design: Design; newIds: string[] } {
  const set = new Set(ids);
  const originals = d.objects.filter((o) => set.has(o.id));
  if (!originals.length) return { design: d, newIds: [] };
  const copies = cloneObjects(originals);
  const lastIndex = Math.max(...originals.map((o) => d.objects.indexOf(o)));
  return { design: addObjects(d, copies, lastIndex + 1), newIds: copies.map((c) => c.id) };
}

/** Moves an object to a new index in the sewing order. */
export function moveObject(d: Design, id: string, toIndex: number): Design {
  const from = d.objects.findIndex((o) => o.id === id);
  if (from < 0) return d;
  const to = Math.max(0, Math.min(d.objects.length - 1, toIndex));
  if (from === to) return d;
  const objects = d.objects.slice();
  const [o] = objects.splice(from, 1);
  objects.splice(to, 0, o);
  return { ...d, objects };
}

/** Moves the given objects one step earlier (-1) or later (+1) in the sewing order, keeping their relative order. */
export function shiftObjects(d: Design, ids: string[], dir: -1 | 1): Design {
  const set = new Set(ids);
  const objects = d.objects.slice();
  const order = dir < 0 ? objects.map((_, i) => i) : objects.map((_, i) => objects.length - 1 - i);
  let changed = false;
  for (const i of order) {
    const j = i + dir;
    if (!set.has(objects[i].id) || j < 0 || j >= objects.length || set.has(objects[j].id)) continue;
    [objects[i], objects[j]] = [objects[j], objects[i]];
    changed = true;
  }
  return changed ? { ...d, objects } : d;
}

export function setStitchType(o: EmbObject, type: StitchType): EmbObject {
  if (o.params.type === type) return o;
  return { ...o, params: defaultParams(type) };
}

export function translateObjects(d: Design, ids: string[], dx: number, dy: number): Design {
  return updateObjects(d, ids, (o) =>
    o.locked
      ? o
      : {
          ...o,
          transform: { ...o.transform, x: o.transform.x + dx, y: o.transform.y + dy },
          entry: o.entry ? { x: o.entry.x + dx, y: o.entry.y + dy } : null,
          exit: o.exit ? { x: o.exit.x + dx, y: o.exit.y + dy } : null,
        },
  );
}

/** Sets a transform while keeping a local anchor point fixed in design space. */
export function withTransform(o: EmbObject, t: Transform): EmbObject {
  return { ...o, transform: t };
}

/** Local centre of the shape (rotation/scale pivot). */
export function localCenter(o: EmbObject): Vec {
  const b = localBBox(o.shape);
  return { x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2 };
}

// ---------------------------------------------------------------- palette

export function addColor(d: Design, color: ThreadColor): Design {
  return { ...d, palette: [...d.palette, color] };
}

export function updateColor(d: Design, id: string, patch: Partial<ThreadColor>): Design {
  return { ...d, palette: d.palette.map((c) => (c.id === id ? { ...c, ...patch } : c)) };
}

/** Recolours all objects of `fromId` to `toId` and removes `fromId` from the palette. */
export function mergeColors(d: Design, fromId: string, toId: string): Design {
  if (fromId === toId) return d;
  const objects = d.objects.map((o) => (o.colorId === fromId ? { ...o, colorId: toId } : o));
  return { ...d, objects, palette: d.palette.filter((c) => c.id !== fromId) };
}

export function removeUnusedColors(d: Design): Design {
  const used = new Set(d.objects.map((o) => o.colorId));
  const palette = d.palette.filter((c) => used.has(c.id));
  return palette.length === d.palette.length ? d : { ...d, palette };
}

export function colorUsage(d: Design): Map<string, number> {
  const m = new Map<string, number>();
  for (const o of d.objects) m.set(o.colorId, (m.get(o.colorId) ?? 0) + 1);
  return m;
}

/** Reorders objects so that all objects of each colour are grouped (stable, in palette order). */
export function groupByColor(d: Design): Design {
  const order = new Map(d.palette.map((c, i) => [c.id, i]));
  const objects = d.objects
    .map((o, i) => ({ o, i }))
    .sort((a, b) => (order.get(a.o.colorId) ?? 1e9) - (order.get(b.o.colorId) ?? 1e9) || a.i - b.i)
    .map((x) => x.o);
  return { ...d, objects };
}

/** Scales the whole design uniformly around the origin (used for design size changes). */
export function scaleDesign(d: Design, s: number): Design {
  if (!(s > 0) || s === 1) return d;
  return {
    ...d,
    widthMm: d.widthMm * s,
    heightMm: d.heightMm * s,
    objects: d.objects.map((o) => ({
      ...o,
      transform: { ...o.transform, x: o.transform.x * s, y: o.transform.y * s, scaleX: o.transform.scaleX * s, scaleY: o.transform.scaleY * s },
      entry: o.entry ? { x: o.entry.x * s, y: o.entry.y * s } : null,
      exit: o.exit ? { x: o.exit.x * s, y: o.exit.y * s } : null,
    })),
    backdrop: d.backdrop ? { ...d.backdrop, x: d.backdrop.x * s, y: d.backdrop.y * s, widthMm: d.backdrop.widthMm * s, heightMm: d.backdrop.heightMm * s } : null,
  };
}
